/**
 * Authentication Context
 * Manages authentication state across the application
 * Uses session cookies from the Python backend for auth
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AuthContextType, AuthState, User } from '../types/auth';
import { authApi } from '../services/api';
import { clearCSRFToken } from '../utils/csrf';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  /**
   * Check authentication status by validating session with backend
   */
  const checkAuth = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Call backend to validate session (session cookie sent automatically)
      const response = await authApi.getProfile();
      
      // Backend returns: { success: true, data: { user_id, org_id, email, name, roles, onboarding_complete } }
      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        const user: User = {
          userId: data.user_id,
          orgId: data.org_id,
          email: data.email,
          name: data.name,
          roles: data.roles,
          onboardingComplete: data.onboarding_complete,
        };
        
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user,
          error: null,
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
      }
    } catch (error) {
      console.error('[Auth] Session validation failed:', error);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: 'Session validation failed',
      });
    }
  };

  /**
   * Redirect to login page (handled by Python backend)
   */
  const login = () => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const finalRedirectUrl = encodeURIComponent(window.location.origin + window.location.pathname);
    
    // Redirect to Python backend login with return URL
    window.location.href = `${apiBaseUrl}/login?final_redirect_url=${finalRedirectUrl}`;
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await authApi.logout();

      // Clear cached CSRF token
      clearCSRFToken();

      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });

      // Redirect to login
      login();
    } catch (error) {
      console.error('[Auth] Logout failed:', error);
      // Even if logout API fails, clear local state and redirect
      clearCSRFToken();
      
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
      login();
    }
  };

  // Check auth status on mount (skip for public routes)
  useEffect(() => {
    // Don't check auth on public routes
    const publicRoutes = ['/', '/auth/callback'];
    const currentPath = window.location.pathname;
    
    if (!publicRoutes.includes(currentPath)) {
      checkAuth();
    } else {
      // For public routes, just mark loading as false
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

